import { CheckIcon } from "@heroicons/react/16/solid";
import { HeartIcon } from "@heroicons/react/24/solid";
import type { VideoModel } from "@ziggapp/klap-core/video";
import { ComponentPropsWithoutRef, FC } from "react";
import { EditorHelper } from "../helpers/editor";
import { useReadPublications } from "../hooks/usePublications";
import { PublicationModel } from "../types/models";
import { convertMicrosecondsToMinSec } from "../utils/string";
import Typography from "./Typography";
import VideoPublicationSummary from "./VideoPublicationSummary";

type VideoCardProps = {
  video: VideoModel;
  selected?: boolean;
  /**
   * Dims the thumbnail and title to the posts column's published level
   * (0.65), on hover too: a locked tile cannot be toggled in any mode.
   * Selection ring, check, and status caption are independent.
   */
  dimmed?: boolean;
  count?: number;
  publications?: PublicationModel[];
  /** @default "m" */
  size?: "s" | "m";
} & ComponentPropsWithoutRef<"div">;

const VideoCard: FC<VideoCardProps> = ({
  video,
  selected = false,
  dimmed = false,
  count,
  publications,
  size = "m",
  className,
  ...rest
}) => {
  const isS = size === "s";
  const { data: fetchedPublications } = useReadPublications(video.id, {
    enabled: publications === undefined,
  });
  const resolvedPublications = publications ?? fetchedPublications;

  const duration = EditorHelper.getLongestTrackDuration(video.tracks);
  const dimClass = dimmed ? "opacity-[0.65]" : "";

  return (
    <div className={`group relative ${className ?? ""}`} {...rest}>
      <div
        className={`aspect-video w-full bg-slate-150 overflow-hidden relative ${
          isS ? "rounded-lg mb-1.5" : "rounded-2xl mb-2"
        }`}
        style={{
          aspectRatio: video.width / video.height,
        }}
      >
        <img
          src={`${process.env.NEXT_PUBLIC_API_URL}/projects/${video.id}/thumbnail`}
          className={`object-cover h-full w-full bg-slate-200 ${dimClass}`}
          alt=""
        />
        <div
          className={`absolute w-full top-0 bg-gradient-to-b from-black/25 to-black/0 gap-1 flex items-center ${
            isS ? "p-1.5" : "p-2"
          }`}
        >
          {video.likedByAuthor && (
            <HeartIcon
              className={`text-white ${isS ? "w-4 h-4" : "w-5 h-5"}`}
            />
          )}
        </div>
        <div
          className={`absolute w-full bottom-0 bg-gradient-to-t from-black/25 to-black/0 gap-1 flex items-center text-white ${
            isS ? "p-1.5" : "p-2"
          }`}
        >
          <Typography
            variant="overline"
            className={`bg-white/10 backdrop-blur-md text-white rounded-full ml-auto ${
              isS ? "px-1.5 py-0.5 !text-[10px] leading-tight" : "px-2 py-1"
            }`}
          >
            {convertMicrosecondsToMinSec(duration)}
          </Typography>
        </div>
        {selected && (
          <div
            className={`absolute rounded-full bg-blue-500 flex items-center justify-center pointer-events-none ${
              isS ? "top-1.5 right-1.5 w-4 h-4" : "top-2 right-2 w-5 h-5"
            }`}
          >
            <CheckIcon
              className={`text-white ${isS ? "size-3" : "size-4"}`}
            />
          </div>
        )}
      </div>
      <div>
        <Typography
          variant={isS ? "subtitle3" : "subtitle2"}
          className={`line-clamp-2 ${dimClass} ${
            selected
              ? "text-slate-950 dark:text-foreground !font-medium"
              : "text-slate-400"
          }`}
        >
          {typeof count === "number" && (
            <span className="text-slate-500 mr-1">{`#${count}`}</span>
          )}
          {video.name}
        </Typography>
        {resolvedPublications && resolvedPublications.length > 0 && (
          <VideoPublicationSummary publications={resolvedPublications} />
        )}
      </div>
    </div>
  );
};

export default VideoCard;
